import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import {
  isIncorrectPassword,
  isPasswordException,
  loadPdfDocument,
} from "../core/pdf/loadDocument";
import { logClientError } from "../utils/logging";

export interface RecentFile {
  path: string;
  name: string;
}

export interface OpenDocument {
  id: string;
  doc: PDFDocumentProxy;
  /** The canonical bytes of the open document. Tools (Milestone B) transform
   *  these and call `replaceBytes` to update the viewer in place. */
  bytes: Uint8Array;
  fileName: string;
  /** Filesystem path when opened from disk (Electron); undefined for web drops. */
  filePath?: string;
  currentPage: number;
  dirty: boolean;
}

/** Pending retry state for an encrypted PDF awaiting its password. */
interface PendingLoad {
  name: string;
  bytes: Uint8Array;
  path?: string;
  markDirty: boolean;
  /** Tab being edited in place (replaceBytes); undefined means "open as a new tab". */
  targetId?: string;
}

export interface PasswordPrompt {
  fileName: string;
  /** True once a submitted password has been rejected. */
  incorrect: boolean;
}

/**
 * Where the drawn signature goes. Shared between the Sign panel (which edits
 * it) and the viewer (which previews it on the page and sets it on click).
 * Fractions are of the page's unrotated width/height, top-left origin.
 */
export interface SignaturePlacement {
  /** 1-based page number. */
  pageNumber: number;
  xFrac: number;
  yFrac: number;
  widthFrac: number;
  /** Captured signature as a PNG data URL; null until the user captures one. */
  image: string | null;
  /** True while the viewer waits for a click to set the position. */
  armed: boolean;
}

const DEFAULT_SIGNATURE: SignaturePlacement = {
  pageNumber: 1,
  xFrac: 0.55,
  yFrac: 0.72,
  widthFrac: 0.3,
  image: null,
  armed: false,
};

interface DocumentState {
  /** The active tab's document, or null when no tabs are open. */
  open: OpenDocument | null;
  /** All open tabs, in the order they were opened. */
  tabs: OpenDocument[];
  activeId: string | null;
  currentPage: number;
  dirty: boolean;
  error: string | null;
  recent: RecentFile[];
  passwordPrompt: PasswordPrompt | null;
  signature: SignaturePlacement;
  // actions
  setSignature: (patch: Partial<SignaturePlacement>) => void;
  openBytes: (name: string, bytes: Uint8Array, path?: string) => Promise<void>;
  openFileObject: (file: File) => Promise<void>;
  openPath: (path: string) => Promise<void>;
  pickFile: () => Promise<void>;
  replaceBytes: (bytes: Uint8Array) => Promise<void>;
  closeDocument: () => void;
  closeTab: (id: string) => void;
  switchTab: (id: string) => void;
  setCurrentPage: (page: number) => void;
  clearError: () => void;
  submitPassword: (password: string) => Promise<void>;
  cancelPasswordPrompt: () => void;
}

const DocumentContext = createContext<DocumentState | null>(null);

/** Best-effort pdf.js cleanup so closing a tab actually frees its memory. */
const destroyDoc = (doc: PDFDocumentProxy | undefined) => {
  try {
    doc?.destroy();
  } catch {
    // Best-effort — the viewer may already be tearing the doc down itself.
  }
};

export const DocumentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [tabs, setTabs] = useState<OpenDocument[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recent, setRecent] = useState<RecentFile[]>([]);
  const [pendingLoad, setPendingLoad] = useState<PendingLoad | null>(null);
  const [passwordPrompt, setPasswordPrompt] = useState<PasswordPrompt | null>(
    null,
  );
  const nextId = useRef(0);
  const [signature, setSignatureState] = useState<SignaturePlacement>(DEFAULT_SIGNATURE);

  const setSignature = useCallback((patch: Partial<SignaturePlacement>) => {
    setSignatureState((cur) => ({ ...cur, ...patch }));
  }, []);

  const open = useMemo(
    () => tabs.find((t) => t.id === activeId) ?? null,
    [tabs, activeId],
  );

  const load = useCallback(
    async (
      name: string,
      bytes: Uint8Array,
      path: string | undefined,
      markDirty: boolean,
      password?: string,
      targetId?: string,
    ) => {
      setError(null);
      try {
        const doc = await loadPdfDocument(bytes, password);
        if (targetId) {
          // Editing an existing tab in place (replaceBytes) — same id, new bytes.
          setTabs((cur) =>
            cur.map((t) => {
              if (t.id !== targetId) return t;
              destroyDoc(t.doc);
              return { ...t, doc, bytes, fileName: name, filePath: path, currentPage: 1, dirty: markDirty };
            }),
          );
        } else {
          const id = `tab-${nextId.current++}`;
          setTabs((cur) => [
            ...cur,
            { id, doc, bytes, fileName: name, filePath: path, currentPage: 1, dirty: markDirty },
          ]);
          setActiveId(id);
        }
        setPendingLoad(null);
        setPasswordPrompt(null);
        if (path && window.electronAPI) {
          await window.electronAPI.addRecentFile(path);
          setRecent(await window.electronAPI.getRecentFiles());
        }
      } catch (err) {
        if (isPasswordException(err)) {
          setPendingLoad({ name, bytes, path, markDirty, targetId });
          setPasswordPrompt({ fileName: name, incorrect: isIncorrectPassword(err) });
          return;
        }
        logClientError("pdf_open_failed", "Failed to open PDF in viewer", {
          error: String(err),
        });
        setError(`Could not open "${name}". It may be corrupt or not a PDF.`);
      }
    },
    [],
  );

  const openBytes = useCallback(
    (name: string, bytes: Uint8Array, path?: string) => load(name, bytes, path, false),
    [load],
  );

  const openFileObject = useCallback(
    async (file: File) => {
      const buf = await file.arrayBuffer();
      await load(file.name, new Uint8Array(buf), undefined, false);
    },
    [load],
  );

  const openPath = useCallback(
    async (path: string) => {
      if (!window.electronAPI) return;
      const res = await window.electronAPI.readFile(path);
      if (res) await load(res.name, res.data, path, false);
    },
    [load],
  );

  const pickFile = useCallback(async () => {
    if (window.electronAPI) {
      const res = await window.electronAPI.pickPdf();
      if (res) await load(res.name, res.data, res.path, false);
      return;
    }
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/pdf,.pdf";
    input.onchange = () => {
      const f = input.files?.[0];
      if (f) openFileObject(f);
    };
    input.click();
  }, [load, openFileObject]);

  // Replace the active tab's bytes after a tool edits them; marks it dirty.
  const replaceBytes = useCallback(
    async (bytes: Uint8Array) => {
      if (!open) return;
      await load(open.fileName, bytes, open.filePath, true, undefined, open.id);
    },
    [open, load],
  );

  const closeTab = useCallback(
    (id: string) => {
      setTabs((cur) => {
        const closing = cur.find((t) => t.id === id);
        destroyDoc(closing?.doc);
        const next = cur.filter((t) => t.id !== id);
        setActiveId((curActive) => {
          if (curActive !== id) return curActive;
          if (!next.length) return null;
          const closedAt = cur.findIndex((t) => t.id === id);
          // Prefer the tab to the left of the one just closed, matching how
          // most tabbed UIs pick the next active tab.
          return next[Math.max(0, closedAt - 1)].id;
        });
        return next;
      });
      setError(null);
    },
    [],
  );

  const closeDocument = useCallback(() => {
    if (activeId) closeTab(activeId);
  }, [activeId, closeTab]);

  const switchTab = useCallback((id: string) => {
    setActiveId(id);
    setError(null);
  }, []);

  const setCurrentPage = useCallback(
    (page: number) => {
      if (!activeId) return;
      setTabs((cur) => cur.map((t) => (t.id === activeId ? { ...t, currentPage: page } : t)));
    },
    [activeId],
  );

  const clearError = useCallback(() => setError(null), []);

  const submitPassword = useCallback(
    async (password: string) => {
      if (!pendingLoad) return;
      await load(
        pendingLoad.name,
        pendingLoad.bytes,
        pendingLoad.path,
        pendingLoad.markDirty,
        password,
        pendingLoad.targetId,
      );
    },
    [pendingLoad, load],
  );

  const cancelPasswordPrompt = useCallback(() => {
    setPendingLoad(null);
    setPasswordPrompt(null);
  }, []);

  // Recent files + "open this PDF" from Explorer / association / drag-drop.
  useEffect(() => {
    if (!window.electronAPI) return;
    window.electronAPI.getRecentFiles().then(setRecent).catch(() => undefined);
    window.electronAPI.onOpenPdf(({ path }) => {
      openPath(path);
    });
  }, [openPath]);

  const value = useMemo<DocumentState>(
    () => ({
      open,
      tabs,
      activeId,
      currentPage: open?.currentPage ?? 1,
      dirty: open?.dirty ?? false,
      error,
      recent,
      passwordPrompt,
      signature,
      setSignature,
      openBytes,
      openFileObject,
      openPath,
      pickFile,
      replaceBytes,
      closeDocument,
      closeTab,
      switchTab,
      setCurrentPage,
      clearError,
      submitPassword,
      cancelPasswordPrompt,
    }),
    [
      open,
      tabs,
      activeId,
      error,
      recent,
      passwordPrompt,
      signature,
      setSignature,
      openBytes,
      openFileObject,
      openPath,
      pickFile,
      replaceBytes,
      closeDocument,
      closeTab,
      switchTab,
      setCurrentPage,
      clearError,
      submitPassword,
      cancelPasswordPrompt,
    ],
  );

  return <DocumentContext.Provider value={value}>{children}</DocumentContext.Provider>;
};

export const useDocument = (): DocumentState => {
  const ctx = useContext(DocumentContext);
  if (!ctx) throw new Error("useDocument must be used within DocumentProvider");
  return ctx;
};
